import { useEffect } from "react";
import { useCard, useToast } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import {
  OrderStatus,
  OrderType,
  useCreateSubscriptionAndAddCardMutation,
  useCreateSubscriptionMutation,
  useGetCheckoutSessionQuery,
} from "../../graphql/generated/schema";
import { usePayment } from "../../hooks/usePayment";

export function useHandleStripeRedirectForEmbeddedCheckout() {
  const {
    paymentFor,
    isPaymentForSubscriptionOnly,
    isPaymentForSubscriptionWithCard,
    isPaymentForSingleCard,
  } = usePayment();
  const { updateCard, cardID, isFree } = useCard();

  const [
    createSubscriptionAndAddCard,
    {
      error: errorCreateSubscriptionAndAddCard,
      loading: isLoadingCreateSubscriptionAndAddCard,
    },
  ] = useCreateSubscriptionAndAddCardMutation();

  const [
    createSubscription,
    { error: errorCreateSubscription, loading: isLoadingCreateSubscription },
  ] = useCreateSubscriptionMutation();

  // HANDLE PAYMENT SUCCESS
  const [searchParams] = useSearchParams();
  // When Stripe redirects after successful payment, it sends a payment status

  const stripeSessionId = searchParams.get("session_id") ?? "";
  const productId = searchParams.get("pid") ?? "";

  const {
    data: sessionData,
    loading: isLoadingGetCheckoutSession,
    error: errorGetCheckoutSession,
  } = useGetCheckoutSessionQuery({
    variables: { input: { sessionId: stripeSessionId } },
    skip: !stripeSessionId,
  });

  const status = sessionData?.getCheckoutSession?.status;
  const isComplete = status === "complete";

  const isLoading =
    isLoadingGetCheckoutSession ||
    isLoadingCreateSubscription ||
    isLoadingCreateSubscriptionAndAddCard;

  /**
   Backend is unaware of Stripe payment being successful. Possibly, the webhook from Stripe to BE failed,
   or it is mid-processing. In either case, we forcefully change the order status of the card in the BE,
   once Stripe redirects us back to the UI with a success message and the card !isFree.
   */
  useEffect(() => {
    if (!isLoadingGetCheckoutSession) {
      const processPaymentInBackend = async () => {
        if (isComplete) {
          try {
            if (isPaymentForSubscriptionOnly) {
              // SUB ONLY
              await createSubscription({
                variables: { input: { productId, stripeSessionId } },
              });
            }

            if (isPaymentForSubscriptionWithCard && cardID && isFree) {
              // SUB WITH CARD
              // Buying a subscription associated with a card.
              // 1. Create a subscription in backend
              // 2. Add card to subscription
              await createSubscriptionAndAddCard({
                variables: {
                  input: { cardId: cardID, productId, stripeSessionId },
                },
              });
            }

            if (isPaymentForSingleCard && cardID && isFree) {
              // SINGLE CARD
              // Is regular single card purchase without subscription
              await updateCard({
                id: cardID,
                orderStatus: OrderStatus.Paid,
                orderType: OrderType.Premium,
              });
            }
          } catch (err) {
            // Handle error if needed
          } finally {
            // setIsLoading(false); // Set loading state to false when mutation is done (whether it succeeded or not)
          }
        }
      };

      processPaymentInBackend();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingGetCheckoutSession, isFree]);

  const processingPaymentInBackendError =
    errorGetCheckoutSession ||
    errorCreateSubscription ||
    errorCreateSubscriptionAndAddCard;

  useToast({
    id: "processingPaymentInBackendError",
    show: !!processingPaymentInBackendError,
    status: "error",
    title: "Error",
    duration: 10000,
    description: "Something went wrong",
  });

  return {
    redirectToPaymentSuccessPage:
      !!paymentFor && !processingPaymentInBackendError && isComplete,
    isProcessingPaymentInBackend: isLoading,
    processingPaymentInBackendError,
  };
}
